import {match, Pattern} from 'ts-pattern';

import {TelephonyCall} from '@dms/api/settings';

import {Nullish} from 'shared/web';

/**
 * What the bottom call bar shows. `connected` is the short green confirmation after the customer
 * or the viewer picked up, before the bar hands the call over to the top-bar chip.
 */
export type TelephonyCallBarPhase =
  'incoming' | 'outboundBrowser' | 'outboundMobile' | 'connected' | 'rejected';

interface TelephonyCallBarContext {
  /** The call this tab saw unsettled and has not handed over to the chip yet. */
  handoverCallId: string | Nullish;
  /** The call the viewer rejected in this tab, shown red for a moment. */
  rejectedCallId: string | Nullish;
}

const UNSETTLED_OUTBOUND_STATUSES = [
  'ringing_user',
  'waiting_for_confirmation',
  'connecting_customer',
] as const;

/**
 * Unsettled = the call still needs the viewer's attention: an inbound call ringing them, or an
 * outbound call nobody has picked up yet. Once talking, the call is settled.
 */
export const isTelephonyCallUnsettled = (call: TelephonyCall) =>
  match(call)
    .with({direction: 'inbound', status: 'ringing'}, () => true)
    .with(
      {direction: 'outbound', status: Pattern.union(...UNSETTLED_OUTBOUND_STATUSES)},
      () => true
    )
    .otherwise(() => false);

/**
 * The bar shows only while the call is unsettled, then once more in green when it gets picked up
 * in front of this tab; a call already talking when the tab first sees it lives in the chip only.
 */
export const getTelephonyCallBarPhase = (
  call: TelephonyCall,
  context: TelephonyCallBarContext
): TelephonyCallBarPhase | null =>
  match(call)
    .when(
      (current) => current.id === context.rejectedCallId,
      () => 'rejected' as const
    )
    .with({direction: 'inbound', status: 'ringing'}, () => 'incoming' as const)
    .with(
      {
        direction: 'outbound',
        channel: 'browser',
        status: Pattern.union(...UNSETTLED_OUTBOUND_STATUSES),
      },
      () => 'outboundBrowser' as const
    )
    .with(
      {
        direction: 'outbound',
        channel: 'mobile',
        status: Pattern.union(...UNSETTLED_OUTBOUND_STATUSES),
      },
      () => 'outboundMobile' as const
    )
    .with({status: 'in_call'}, (current) =>
      current.id === context.handoverCallId ? ('connected' as const) : null
    )
    .otherwise(() => null);
